import React, { useEffect, useState } from "react";
import { api, fmtPKR, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Banknote, Smartphone, Upload, CheckCircle2, XCircle, Hourglass, Copy, Crown, Sparkles, Flame,
} from "lucide-react";
import { toast } from "sonner";
import CurrentPlanCard from "../components/CurrentPlanCard";

const STATUS_TONE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const PAYMENT_METHODS = [
  { id: "bank_transfer", label: "Bank Transfer" },
  { id: "jazzcash", label: "JazzCash" },
  { id: "easypaisa", label: "Easypaisa" },
  { id: "cash", label: "Cash deposit" },
];

const CYCLE_LABEL = {
  quarter: "/ quarter",
  year: "/ year",
  month: "/ month",
  trial: "",
};

const BADGE_STYLES = {
  founding: {
    label: "Founding Member",
    icon: Crown,
    tone: "bg-amber-100 text-amber-900 border-amber-300",
    cardRing: "border-amber-400 ring-2 ring-amber-200 shadow-lg shadow-amber-100",
  },
  popular: {
    label: "Most Popular",
    icon: Flame,
    tone: "bg-emerald-100 text-emerald-900 border-emerald-300",
    cardRing: "border-emerald-500 ring-2 ring-emerald-200 shadow-lg shadow-emerald-100",
  },
  best_value: {
    label: "Best Value",
    icon: Sparkles,
    tone: "bg-sky-100 text-sky-900 border-sky-300",
    cardRing: "border-sky-500 ring-2 ring-sky-200 shadow-lg shadow-sky-100",
  },
};

function copyToClipboard(text) {
  if (!text) return;
  navigator.clipboard.writeText(text);
  toast.success("Copied");
}

export default function Subscription() {
  const [bank, setBank] = useState(null);
  const [plans, setPlans] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [founding, setFounding] = useState({ remaining: 100, claimed: 0, total_spots: 100 });
  const [planId, setPlanId] = useState("founding");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [txnId, setTxnId] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/payments/submissions").then((r) => setSubmissions(r.data));

  useEffect(() => {
    api.get("/platform/bank").then((r) => setBank(r.data)).catch(() => {});
    api.get("/distributors/me").catch(() => {});
    api.get("/platform/founding-stats").then((r) => setFounding(r.data)).catch(() => {});
    load();
    // Dynamic plan catalog (public endpoint) — no more hardcoded list.
    api.get("/platform/plans")
      .then((r) => {
        const all = (r.data || []).filter((p) => (p.price_pkr || 0) > 0);
        setPlans(all);
        // Default selection: founding if spots remain, else quarterly
        const f = all.find((p) => p.id === "founding");
        if (f) setPlanId("founding");
        else if (all[0]) setPlanId(all[0].id);
      })
      .catch(() => setPlans([]));
  }, []);

  useEffect(() => {
    const p = plans.find((x) => x.id === planId);
    if (p) setAmount(String(p.price_pkr || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, plans]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("plan_id", planId);
      fd.append("amount", String(amount));
      fd.append("payment_method", method);
      fd.append("transaction_id", txnId);
      fd.append("notes", notes);
      if (file) fd.append("screenshot", file);
      await api.post("/payments/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Payment submitted · admin will verify shortly");
      setTxnId(""); setNotes(""); setFile(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const pendingExists = submissions.some((s) => s.status === "pending");

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Subscription & Plan Upgrade</h1>
        <p className="text-slate-500 mt-1">Apna plan upgrade karein — bank/JazzCash/Easypaisa par payment karein, screenshot upload karein, admin verify karega.</p>
      </div>

      {/* Current plan summary — trial countdown, usage bars, renew CTA */}
      <CurrentPlanCard />

      {/* Plans catalog */}
      {plans.length > 0 && (
        <div data-testid="plans-catalog">
          <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
            <div className="font-display font-semibold text-lg">Choose Your Plan</div>
            {founding.remaining > 0 && (
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-300 text-amber-900 px-3 py-1 text-xs font-bold" data-testid="founding-spots-banner">
                <Crown className="w-3.5 h-3.5" />
                {founding.remaining} / {founding.total_spots} Founding Spots Left
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {plans.map((p) => {
              const isFoundingClosed = p.id === "founding" && founding.remaining <= 0;
              const badge = BADGE_STYLES[p.badge];
              const selected = planId === p.id;
              const ringCls = selected
                ? badge?.cardRing || "border-emerald-500 ring-2 ring-emerald-200"
                : "border-slate-200";
              return (
                <div
                  key={p.id}
                  className={`relative bg-white border rounded-md p-5 sm:p-6 flex flex-col transition ${ringCls} ${isFoundingClosed ? "opacity-60" : ""}`}
                  data-testid={`plan-card-${p.id}`}
                >
                  {badge && (
                    <div className={`absolute -top-3 left-4 inline-flex items-center gap-1 rounded-full ${badge.tone} border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider`} data-testid={`plan-badge-${p.id}`}>
                      <badge.icon className="w-3 h-3" />
                      {badge.label}
                    </div>
                  )}
                  <div className="font-display font-bold text-xl">{p.name}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-slate-500 text-sm">PKR</span>
                    <span className="font-display text-3xl sm:text-4xl font-bold tabular text-slate-900">
                      {Number(p.price_pkr).toLocaleString()}
                    </span>
                    <span className="text-slate-500 text-sm">
                      {CYCLE_LABEL[p.billing_cycle] || "/ quarter"}
                    </span>
                  </div>
                  {p.id === "annual" && (
                    <div className="mt-1 text-xs font-semibold text-sky-700">💰 Save PKR 2,000 vs Quarterly</div>
                  )}
                  {p.id === "founding" && (
                    <div className="mt-1 text-xs font-semibold text-amber-800">🔒 Lifetime price lock-in</div>
                  )}
                  <ul className="mt-4 space-y-2 text-sm text-slate-700 flex-1">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    type="button"
                    disabled={isFoundingClosed}
                    onClick={() => { setPlanId(p.id); document.getElementById("sub-form-anchor")?.scrollIntoView({ behavior: "smooth" }); }}
                    className={`mt-5 w-full ${selected ? "bg-emerald-700 hover:bg-emerald-800" : "bg-slate-900 hover:bg-slate-800"}`}
                    data-testid={`plan-select-${p.id}`}
                  >
                    {isFoundingClosed ? "Spots full" : selected ? "Selected ✓" : "Select Plan"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {pendingExists && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-center gap-3" data-testid="sub-pending-banner">
          <Hourglass className="w-5 h-5 text-amber-700" />
          <div className="text-sm">
            <div className="font-semibold text-amber-800">Payment pending verification</div>
            <div className="text-amber-700">Admin aap ki payment review kar rahe hain. Approval ke baad plan turant active ho jaye ga.</div>
          </div>
        </div>
      )}

      {/* Platform bank details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-3" data-testid="sub-bank-details">
          <div className="flex items-center gap-2 text-emerald-700">
            <Banknote className="w-5 h-5" />
            <div className="font-display font-bold text-lg text-slate-900">HisaabPro Bank Details</div>
          </div>
          <p className="text-xs text-slate-500">Yahan payment karein, phir screenshot is page par upload karein.</p>
          {bank ? (
            <div className="space-y-2 text-sm">
              {bank.account_holder && <Row label="Account holder" value={bank.account_holder} />}
              {bank.bank_name && <Row label="Bank" value={bank.bank_name} />}
              {bank.account_number && <Row label="Account #" value={bank.account_number} copy />}
              {bank.iban && <Row label="IBAN" value={bank.iban} copy />}
              {bank.branch && <Row label="Branch" value={bank.branch} />}
              {bank.jazzcash_number && <Row label="JazzCash" value={bank.jazzcash_number} icon={Smartphone} copy />}
              {bank.easypaisa_number && <Row label="Easypaisa" value={bank.easypaisa_number} icon={Smartphone} copy />}
              {Object.keys(bank).length === 0 && <div className="text-slate-500 text-sm">Bank details abhi configure nahi huwe.</div>}
            </div>
          ) : (
            <div className="text-slate-500 text-sm">Loading…</div>
          )}
        </div>

        {/* Submission form */}
        <form id="sub-form-anchor" onSubmit={submit} className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="sub-form">
          <div className="flex items-center gap-2 text-slate-900">
            <Upload className="w-5 h-5 text-emerald-600" />
            <div className="font-display font-bold text-lg">Submit your payment</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Plan</Label>
              <Select value={planId} onValueChange={setPlanId}>
                <SelectTrigger data-testid="sub-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {fmtPKR(p.price_pkr)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount (PKR)</Label>
              <Input type="number" min={1} required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="sub-amount" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Payment method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="sub-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Transaction ID (optional)</Label>
              <Input value={txnId} onChange={(e) => setTxnId(e.target.value)} placeholder="TX12345" data-testid="sub-txn" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Screenshot (jpg/png/pdf, max 10 MB)</Label>
            <Input type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="sub-screenshot" />
          </div>
          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sub-notes" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sub-submit">
            {busy ? "Submitting…" : "Submit for verification"}
          </Button>
        </form>
      </div>

      {/* My submissions */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-4 border-b border-slate-200 font-display font-semibold">Your submissions</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Plan</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-left px-4 py-3 font-semibold">Method</th>
                <th className="text-left px-4 py-3 font-semibold">Txn ID</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Reason / Proof</th>
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No submissions yet.</td></tr>}
              {submissions.map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`sub-row-${s.id}`}>
                  <td className="px-4 py-3 text-xs tabular text-slate-500">{(s.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 capitalize">{s.plan_id}</td>
                  <td className="px-4 py-3 text-right tabular font-semibold">{fmtPKR(s.amount)}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{s.payment_method}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs tabular">{s.transaction_id || "—"}</td>
                  <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold border ${STATUS_TONE[s.status]}`}>
                    {s.status === "approved" && <CheckCircle2 className="w-3 h-3" />}
                    {s.status === "rejected" && <XCircle className="w-3 h-3" />}
                    {s.status === "pending" && <Hourglass className="w-3 h-3" />}
                    {s.status}
                  </span></td>
                  <td className="px-4 py-3 text-xs">
                    {s.status === "rejected" && <span className="text-red-700">{s.reject_reason}</span>}
                    {s.screenshot_url && (
                      <a href={`${API.replace(/\/api$/, "")}${s.screenshot_url}`} target="_blank" rel="noreferrer" className="text-emerald-700 font-semibold hover:underline ml-2" data-testid={`sub-screenshot-${s.id}`}>View proof</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, icon: Icon, copy }) {
  return (
    <div className="flex items-center justify-between bg-slate-50 rounded px-3 py-2.5 text-sm">
      <div className="flex items-center gap-2 text-slate-500">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-xs uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-slate-900 tabular">{value}</span>
        {copy && (
          <button type="button" onClick={() => copyToClipboard(value)} className="text-slate-400 hover:text-slate-700" data-testid={`copy-${label}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
